<template>
	<div class="box">
		<LoadingSpinner v-if="loading" />
		<div v-else>
			<p>{{ $t('compta.fec.intro') }}</p>

			<!-- Le contrôle vient du serveur : c'est lui qui connaît les règles du format, le front
			     n'a pas à les dupliquer. -->
			<b-alert v-if="!can_export" variant="danger" show class="mt-2">
				{{ $t('compta.fec.cant_export') }}
				<div v-for="check in checks" :key="check.code" class="mt-2">
					<strong>{{ $t('compta.fec.check.' + check.code) }}</strong>
					<template v-if="check.items.length && !isFixable(check)"> : {{ check.items.map(i => i.label).join(', ') }}</template>

					<!-- Comptes clients réparables : chacun s'ouvre pour correction manuelle, ou se
					     renumérote depuis le gabarit du plan comptable. -->
					<template v-if="isFixable(check)">
						<b-button variant="outline-primary" size="sm" class="ml-2" :disabled="fixing !== null" @click.prevent="fix(null)">
							<font-awesome-icon :icon="['fal', 'sync-alt']" /> {{ $t('compta.fec.fix_all', {count: check.items.length}) }}
						</b-button>
						<ul class="fec-invalid-accounts">
							<li v-for="item in check.items" :key="item.account">
								<router-link :to="{ name: 'tiersFiche', params: { tiers_id: item.tiers }}">{{ item.label }}</router-link>
								<b-button variant="link" size="sm" :disabled="fixing !== null" :title="$t('compta.fec.fix_one')" @click.prevent="fix([item.account])">
									<font-awesome-icon :icon="['fal', 'sync-alt']" :spin="fixing === item.account" />
								</b-button>
							</li>
						</ul>
					</template>
				</div>
			</b-alert>

			<!-- Génération en cours : l'avancement se lit dans les pièces déjà traitées -->
			<div v-else-if="generating">
				<p><font-awesome-icon :icon="['fas', 'spinner']" pulse /> {{ $t('compta.fec.generating') }}</p>
				<template v-if="progress">
					<label>{{ $t('compta.fec.progress_invoices', {done: progress.invoices_done, total: progress.invoices_total}) }}</label>
					<b-progress :value="progress.invoices_done" :max="progress.invoices_total || 1" class="mb-2" />
					<label>{{ $t('compta.fec.progress_payments', {done: progress.payments_done, total: progress.payments_total}) }}</label>
					<b-progress :value="progress.payments_done" :max="progress.payments_total || 1" />
				</template>
			</div>

			<div v-else class="form-group">
				<!-- La date de départ ne se choisit qu'une fois : elle fige la profondeur d'historique
				     reprise, que les générations suivantes relisent sur le premier export. Ensuite il
				     n'y a plus rien à choisir, le delta détermine seul ce qui sort. -->
				<template v-if="initialising">
					<label for="fec_start">{{ $t('compta.fec.start') }}</label>
					<e-select
						id="fec_start"
						track-by="value"
						label="label"
						v-model="start"
						:options="starts"
						:searchable="false"
						:allow-empty="false"
						:show-labels="false"
					/>
					<small class="d-block text-muted mt-1">{{ $t('compta.fec.start_help') }}</small>
				</template>
				<div class="text-center mt-3">
					<button class="btn btn-primary" type="button" :disabled="processing" @click.prevent="launch">
						<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse />{{ initialising ? $t('compta.fec.generate') : $t('compta.fec.generate_next') }}
					</button>
				</div>
			</div>

			<b-alert v-if="empty_delta" variant="info" show class="mt-2">{{ $t('compta.fec.nothing_new') }}</b-alert>

			<!-- Un compte bloqué n'est pas un échec de la renumérotation : c'est un motif de gabarit
			     incapable de produire des numéros uniques, et l'utilisateur doit savoir quoi changer. -->
			<b-alert v-if="fix_blocked" variant="warning" show class="mt-2">
				{{ $t('compta.fec.fix_blocked', {count: fix_blocked}) }}
			</b-alert>

			<!-- Onboarding : tant que le rapprochement des comptes n'est pas au point, il faut pouvoir
			     tout refaire. Réservé à l'administrateur de la licence, comme les autres réglages
			     comptables sensibles. -->
			<div v-if="is_admin && !generating && exports.length > 0" class="mt-4">
				<b-button variant="outline-danger" size="sm" :disabled="resetting" @click.prevent="confirmReset">
					<font-awesome-icon v-if="resetting" :icon="['fas', 'spinner']" pulse />{{ $t('compta.fec.reset') }}
				</b-button>
				<small class="d-block text-muted mt-1">{{ $t('compta.fec.reset_help') }}</small>
			</div>

			<h3 class="mt-4">{{ $t('compta.fec.history') }}</h3>
			<p v-if="exports.length === 0">{{ $t('compta.fec.no_history') }}</p>
			<b-table v-else :items="exports" :fields="fields" striped hover>
				<template v-slot:cell(fecexport_generated_at)="data">{{ formatDate(data.value) }}</template>
				<template v-slot:cell(fecexport_start)="data">{{ data.value ? formatDate(data.value) : '' }}</template>
				<template v-slot:cell(fecexport_end)="data">{{ formatDate(data.value) }}</template>
				<template v-slot:cell(download)="data">
					<button class="btn btn-secondary btn-sm" type="button" :disabled="downloading === data.item.fecexport_id" @click.prevent="download(data.item)">
						<font-awesome-icon :icon="['fas', 'download']" />
					</button>
				</template>
			</b-table>
		</div>

		<ModalConfirm
			ref="reset_modal"
			:text_title="$t('compta.fec.reset_title')"
			:text_question="$t('compta.fec.reset_question')"
			:text_message="$t('compta.fec.reset_message')"
			:text_button_ok="$t('compta.fec.reset')"
			:text_button_cancel="$t('modal.general.annuler')"
			:icon="['fal', 'trash-alt']"
			:callback_validation="reset"
		/>
	</div>
</template>

<script type="text/javascript">
	import Accounting from "@/mixins/Accounting.js"
	import Common from '@/assets/js/common.js'

	export default {
		name: 'AccountingFec',
		mixins: [Accounting],
		props: ['accountingplan_id'],
		data () {
			return {
				accounting: null,
				exports: [],
				checks: [],
				fixing: null,
				fix_blocked: 0,
				loading: true,
				processing: false,
				generating: false,
				progress: null,
				empty_delta: false,
				start: null,
				downloading: null,
				resetting: false,
				poll_interval: null,
				fields: [
					{ key: 'fecexport_generated_at', label: this.$t('compta.fec.generated_at') },
					{ key: 'fecexport_start', label: this.$t('compta.fec.from') },
					{ key: 'fecexport_end', label: this.$t('compta.fec.to') },
					{ key: 'fecexport_lines', label: this.$t('compta.fec.lines') },
					{ key: 'fecexport_filename', label: this.$t('compta.fec.filename') },
					{ key: 'download', label: '' }
				]
			}
		},

		mounted() {
			this.init_component()
		},

		beforeDestroy() {
			this.stopPolling()
		},

		methods: {
			async init_component() {
				this.loading = true
				this.accounting = await this.loadAccountingPlan(this.accountingplan_id)
				this.start = this.starts[this.starts.length - 1]
				await this.refresh()
				this.loading = false

				if(this.generating) {
					this.startPolling()
				}
			},

			/**
			 * Sérialise une date en AAAA-MM-JJ à partir de ses composantes locales.
			 *
			 * toISOString() ne conviendrait pas : il bascule en UTC et décale donc d'un jour toute date
			 * construite à minuit sur un fuseau en avance sur Greenwich — une fin de mois deviendrait
			 * l'avant-dernier jour.
			 * @param  {Date} date
			 * @return {String}
			 */
			toIsoDate(date) {
				const month = ('0' + (date.getMonth() + 1)).slice(-2)
				const day = ('0' + date.getDate()).slice(-2)

				return date.getFullYear() + '-' + month + '-' + day
			},

			async refresh() {
				const status = await this.getFecStatus(this.accountingplan_id)

				if(!status) return false

				const was_generating = this.generating

				this.generating = status.generating
				this.progress = status.progress
				this.exports = status.exports
				this.checks = status.checks

				// La génération vient de se terminer : si aucun fichier n'en est sorti, c'est que rien
				// n'avait bougé depuis la dernière livraison.
				if(was_generating && !this.generating) {
					this.empty_delta = this.exports.length === 0 || !this.isRecent(this.exports[0])
				}
			},

			/**
			 * Un export produit à l'instant, par opposition à celui d'une livraison précédente restée
			 * en tête de liste parce que la génération n'a rien trouvé de neuf.
			 */
			isRecent(fec_export) {
				return (new Date() - new Date(fec_export.fecexport_generated_at)) < 120000
			},

			async launch() {
				if(this.processing) return false
				this.processing = true
				this.empty_delta = false

				await this.exportFec(this.accountingplan_id, this.initialising ? this.start.value : null)

				this.generating = true
				this.processing = false
				this.startPolling()
			},

			async download(fec_export) {
				if(this.downloading) return false
				this.downloading = fec_export.fecexport_id

				const result = await this.downloadFec(this.accountingplan_id, fec_export.fecexport_id)

				if(result) {
					Common.csvToFile(result.content, result.filename)
				}

				this.downloading = null
			},

			confirmReset() {
				this.$refs.reset_modal.openModal()
			},

			async reset() {
				if(this.resetting) return false
				this.resetting = true

				await this.resetFec(this.accountingplan_id)

				this.$refs.reset_modal.closeModal()
				this.empty_delta = false
				await this.refresh()
				this.resetting = false
			},

			/**
			 * Seuls les comptes clients se réparent tout seuls, et à condition que le gabarit de
			 * numérotation du plan soit lui-même conforme — sinon le contrôle remonte
			 * invalid_client_account_template et il n'y a rien à régénérer.
			 */
			isFixable(check) {
				return check.code === 'invalid_client_account'
			},

			async fix(account_ids) {
				if(this.fixing !== null) return false
				this.fixing = account_ids ? account_ids[0] : 'all'
				this.fix_blocked = 0

				const result = await this.fixFecClientAccounts(this.accountingplan_id, account_ids)

				if(result) {
					this.fix_blocked = result.blocked
				}

				await this.refresh()

				this.fixing = null
			},

			startPolling() {
				this.stopPolling()
				this.poll_interval = setInterval(async () => {
					await this.refresh()

					if(!this.generating) {
						this.stopPolling()
					}
				}, 3000)
			},

			stopPolling() {
				if(this.poll_interval) {
					clearInterval(this.poll_interval)
					this.poll_interval = null
				}
			}
		},

		computed: {
			/**
			 * Une comptabilité qui n'a encore rien livré : c'est le seul moment où la date de départ
			 * se choisit, puisqu'elle est ensuite figée sur le premier export.
			 */
			initialising() {
				return this.exports.length === 0
			},

			/**
			 * Départs proposés : les débuts de mois de l'année écoulée.
			 *
			 * La liste s'arrête volontairement à douze mois. Reprendre tout l'historique produirait un
			 * premier fichier de plusieurs exercices, que l'expert-comptable n'a pas à réintégrer, et
			 * proposer une date plus ancienne laisserait croire qu'elle serait couverte.
			 */
			starts() {
				const options = []
				const today = new Date()

				for (let i = 0; i < 12; i++) {
					const first_of_month = new Date(today.getFullYear(), today.getMonth() - i, 1)
					options.push({
						value: this.toIsoDate(first_of_month),
						label: first_of_month.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
					})
				}

				return options
			},

			is_admin() {
				return this.$store.state.userAccess.hasLicenceAdminAccess
			},

			can_export() {
				return this.checks.length === 0
			}
		},

		components: {
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
			ModalConfirm: () => import('GroomyRoot/components/Modals/ModalConfirm')
		}
	}
</script>
